import React, { useState } from 'react';
import {Dialog, DialogContent, DialogTitle, TextField, IconButton, Button} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import queryLLM  from './QueryLLM';
import { useMapContext } from '../../../contexts/MapContext';
import {useLIDContext} from "../../../contexts/LIDContext";

const SYSTEM_PROMPT = `
Your role is to convert a user's query into a set of rules that can be applied to a subcatchment.
This will be used to select a set of subcatchments to add green infrastructure to.

Available Parameters:
percent_impervious: Range[0, 99.97] % Impervious Surface
elevation: Range[567.63, 671.29] Average elevation
soil_types: Options[Clay, Sand, Loam034, Loam032, Loam037] Soil Type

You may specify a maximum and/or a minimum value for each parameter.

*Important* If a parameter should be ignored, use a value of -1 or an empty list.
`

const LIDAgent = () => {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [processingQuery, setProcessingQuery] = useState(false);

  const {
      planIsRunning,
      outFileExists,
      geoJSONData,
      setEnableLIDpanel,
      editMode,
      setBrushMode,
      setEraserMode,
      setHandMode,
      setPolygonMode,
      setAddGIFromBrush,
      setLIDMode,
      setEditMode,
  } = useMapContext();
    const {
        brushedFeaturesData,
        brushedSubcatchments,
        setBrushedSubcatchments,
        setBrushedFeaturesData,
    } = useLIDContext();

    const OPENROUTER_API_KEY = process.env.REACT_APP_OPENROUTER_API_KEY;
    if (OPENROUTER_API_KEY === "none") {
        return null;
    }

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

    const handleSubmit = async () => {
        try {
            if (!prompt) return;
            setOpen(false);
            setProcessingQuery(true);
            const rules = await queryLLM(prompt, SYSTEM_PROMPT);
            console.log("LLM Response: ", rules);

            function rulesFilter(subcatchment) {
                if (rules["percent_impervious"]["min"] !== -1.0) {
                    if (subcatchment["properties"]["IMPERV"] < rules["percent_impervious"]["min"]) {
                        return false;
                    }
                }
                if (rules["percent_impervious"]["max"] !== -1.0) {
                    if (subcatchment["properties"]["IMPERV"] > rules["percent_impervious"]["max"]) {
                        return false;
                    }
                }
                if (rules["elevation"]["min"] !== -1.0) {
                    if (subcatchment["properties"]["WTELEV"] < rules["elevation"]["min"]) {
                        return false;
                    }
                }
                if (rules["elevation"]["max"] !== -1.0) {
                    if (subcatchment["properties"]["WTELEV"] > rules["elevation"]["max"]) {
                        return false;
                    }
                }
                if (rules["soil_types"].length > 0) {
                    if (!rules["soil_types"].includes(subcatchment["properties"]["AQUIFER"])) {
                        return false;
                    }
                }
                return true;
            }

            // Effectively a brush click
            if (!editMode) setEditMode(true);
            setEnableLIDpanel(true);
            setBrushMode(true);
            setEraserMode(false);
            setHandMode(false);
            setPolygonMode(false);
            setAddGIFromBrush(true);
            setLIDMode(true);

            const filteredSubcatchments = geoJSONData.subcatchments.features.filter(subcatchment => rulesFilter(subcatchment));

            const tempBrushedSubcatchments = new Set(filteredSubcatchments.map(subcatchment => subcatchment.properties.NAME));
            const tempBrushedFeaturesData = Object.fromEntries(filteredSubcatchments.map(subcatchment => [subcatchment.properties.NAME, subcatchment.properties]));

            setBrushedSubcatchments(tempBrushedSubcatchments);
            setBrushedFeaturesData(tempBrushedFeaturesData);

        } finally {
            setProcessingQuery(false);
        }

    }

    if (planIsRunning || outFileExists) return null;

  return (
    <>
      <IconButton
        onClick={handleOpen}
        sx={{
          width:42,
          height: 42,
            position: 'absolute',
            top: '180px',
            left: '18px',
            zIndex: 1100,
          backgroundColor: '#1E90FF',
          color: 'white',
          '&:hover': {
            backgroundColor: 'primary.dark',
          },
        }}
      >
        <SmartToyIcon />
      </IconButton>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>What areas would you like to select?</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Include measures like impervious, elevation, soil type."
            fullWidth
            multiline
            rows={4}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
          <Button disabled={prompt === ""} onClick={handleSubmit} variant="contained" color="primary">Submit</Button>
      </Dialog>
        <Dialog open={processingQuery} maxWidth="sm" fullWidth>
            <DialogContent>
                Running query
            </DialogContent>
        </Dialog>
    </>
  );
};

export default LIDAgent;
